import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Trash2, Mail, Phone, Linkedin, Building2,
  Briefcase, Loader2, User
} from "lucide-react";
import { CONTACT_SOURCES, CONTACT_SOURCE_LABELS } from "../lib/constants";

export default function ContactDetailPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const [cRes, compRes, dRes] = await Promise.all([
        axios.get(`${API}/contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/deals?search=`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setContact(cRes.data);
      setForm(cRes.data);
      setCompanies(compRes.data);
      setDeals(dRes.data.filter(d => d.primary_contact_id === id));
    } catch { toast.error("Failed to load contact"); navigate("/contacts"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { first_name: form.first_name, last_name: form.last_name, role: form.role, email: form.email, phone: form.phone, linkedin: form.linkedin, notes: form.notes, company_id: form.company_id || null, source: form.source };
      const res = await axios.put(`${API}/contacts/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setContact(res.data);
      setEditing(false);
      toast.success("Contact updated");
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contact deleted");
      navigate("/contacts");
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>;
  if (!contact) return null;

  const f = editing ? form : contact;

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/contacts" className="p-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="font-display text-2xl text-white" data-testid="contact-detail-name">{contact.full_name}</h1>
          <p className="text-white/40 text-sm">{contact.role}{contact.company_name ? ` at ${contact.company_name}` : ""}</p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button onClick={() => { setEditing(false); setForm(contact); }} className="px-4 py-2 text-sm text-white/50 hover:text-white bg-white/5 rounded-lg">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-accent flex items-center gap-2 text-sm" data-testid="save-contact-detail-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="px-4 py-2 text-sm text-white bg-white/5 hover:bg-white/10 rounded-lg" data-testid="edit-contact-btn">Edit</button>
              <button onClick={handleDelete} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg" data-testid="delete-contact-btn"><Trash2 className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="glass-card-static p-6">
            <h3 className="text-white/50 text-xs uppercase tracking-wider mb-4">Contact Information</h3>
            {editing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="form-label">First Name</label><input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} className="form-input" /></div>
                  <div><label className="form-label">Last Name</label><input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} className="form-input" /></div>
                </div>
                <div><label className="form-label">Role</label><input value={form.role || ""} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="form-input" /></div>
                <div><label className="form-label">Email</label><input value={form.email || ""} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="form-input" /></div>
                <div><label className="form-label">Phone</label><input value={form.phone || ""} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="form-input" /></div>
                <div><label className="form-label">LinkedIn</label><input value={form.linkedin || ""} onChange={e => setForm(p => ({ ...p, linkedin: e.target.value }))} className="form-input" /></div>
                <div><label className="form-label">Company</label>
                  <select value={form.company_id || ""} onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))} className="form-input">
                    <option value="">No Company</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </select>
                </div>
                <div><label className="form-label">Source</label>
                  <select value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} className="form-input">
                    {CONTACT_SOURCES.map(s => <option key={s} value={s}>{CONTACT_SOURCE_LABELS[s]}</option>)}
                  </select>
                </div>
                <div><label className="form-label">Notes</label><textarea value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="form-input min-h-[100px]" /></div>
              </div>
            ) : (
              <div className="space-y-3">
                {f.email && <div className="flex items-center gap-3 text-white/70"><Mail className="w-4 h-4 text-white/30" /><a href={`mailto:${f.email}`} className="hover:text-[#C9A96E] transition-colors">{f.email}</a></div>}
                {f.phone && <div className="flex items-center gap-3 text-white/70"><Phone className="w-4 h-4 text-white/30" /><a href={`tel:${f.phone}`} className="hover:text-[#C9A96E] transition-colors">{f.phone}</a></div>}
                {f.linkedin && <div className="flex items-center gap-3 text-white/70"><Linkedin className="w-4 h-4 text-white/30" /><a href={f.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A96E] transition-colors truncate">{f.linkedin}</a></div>}
                {f.company_name && <div className="flex items-center gap-3 text-white/70"><Building2 className="w-4 h-4 text-white/30" /><Link to={`/companies/${f.company_id}`} className="hover:text-[#C9A96E] transition-colors">{f.company_name}</Link></div>}
                {f.notes && <div className="mt-4 pt-4 border-t border-white/5"><p className="text-white/50 text-sm whitespace-pre-wrap">{f.notes}</p></div>}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="glass-card-static p-6">
            <h3 className="text-white/50 text-xs uppercase tracking-wider mb-3">Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-white/40">Source</span><span className="text-white/70">{CONTACT_SOURCE_LABELS[f.source] || f.source}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Created</span><span className="text-white/70">{new Date(f.created_at).toLocaleDateString()}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Updated</span><span className="text-white/70">{new Date(f.updated_at).toLocaleDateString()}</span></div>
            </div>
          </div>

          <div className="glass-card-static p-6">
            <h3 className="text-white/50 text-xs uppercase tracking-wider mb-3">Linked Deals</h3>
            {deals.length === 0 ? (
              <p className="text-white/30 text-sm">No deals linked</p>
            ) : (
              <div className="space-y-2">
                {deals.map(d => (
                  <Link key={d.id} to={`/deals/${d.id}`} className="block p-3 bg-white/[0.03] rounded-lg hover:bg-white/[0.06] transition-colors">
                    <p className="text-white text-sm font-medium truncate">{d.title}</p>
                    <p className="text-white/40 text-xs">{d.status_label} · {d.value_estimate > 0 ? `€${d.value_estimate.toLocaleString()}` : "No value"}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
